// A sign-in that routes to the registration form and cannot finish it.
//
// Firebase guards `updatePassword` with a recency rule read off `auth_time`, so
// a player who spends long enough filling the form in has their password change
// refused — and no retry can help, because every attempt is made with a sign-in
// older than the last. `RegisterPage` answers that with the screen that hands
// out new links.
//
// That answer used to live in component state, which a reload throws away. The
// session behind it does not: the traders token is in localStorage and the
// Firebase sign-in is stored with the SDK default, so both come back, the guard
// routes to the registration form again, and the player is handed the form they
// were just told to give up on. They fill it in, it is refused for the same
// reason, and they are back where they started — a loop with no exit in it but
// the one press they have already been talked out of taking.
//
// So the fact is written down beside the session it describes, and outlives the
// mount that discovered it.
//
// Worth being exact about what is stale here: it is the Firebase sign-in, not
// the traders token, which is a perfectly valid session for everything the user
// system offers. What makes it worth neutering anyway is that a player with an
// unfinished registration has nothing else to do with it — setting a password is
// the whole of what this session is for, so a session that cannot set one is
// only good for routing them somewhere that will refuse them.
const STORAGE_KEY = 'registrationNeedsNewLink';

// Every accessor is guarded. Storage throws on access in a private window and in
// a browser with site data blocked, and this is read on the way into a screen
// that is already telling the player something has gone wrong — a second failure
// on top of the first must not be what they see.

/** Written when Firebase refuses the password change as too old a sign-in. */
export function markSignInUnusable() {
	try {
		localStorage.setItem(STORAGE_KEY, 'true');
	}
	catch (error) {
		// Nothing to do about it. The screen still renders from component state
		// for this mount; only surviving a reload is lost.
		console.error('Could not record the unusable sign-in.', error);
	}
}

/**
 * Read by `RegisterPage` as it mounts, to decide between the form and the screen
 * that hands out new links.
 */
export function isSignInUnusable() {
	try {
		return localStorage.getItem(STORAGE_KEY) === 'true';
	}
	catch (error) {
		// Read as "no". The form is the screen the player came for, and showing it
		// costs them one refused submit, which puts the recovery screen up by the
		// ordinary route. Reading it as "yes" would put the recovery screen in
		// front of every player whose browser blocks storage.
		console.error('Could not read the unusable sign-in.', error);
		return false;
	}
}

/**
 * Cleared by the two things that make it untrue: a new link being exchanged,
 * which is the fresh sign-in this whole screen exists to obtain, and a logout,
 * which takes the session it describes with it.
 */
export function clearUnusableSignIn() {
	try {
		localStorage.removeItem(STORAGE_KEY);
	}
	catch (error) {
		console.error('Could not clear the unusable sign-in.', error);
	}
}
